// Browser Desk console — HTTP client.
//
// Same apiFetch pattern as StateData/api.js: getUser() -> Bearer token ->
// REACT_APP_INTEGRATION_MICROSERVICE_URL. The desk exists only in production, so the
// backend reads production state whatever environment the token is for.

import { getUser } from '../../../auth/authService';

const INTEGRATION_URL = process.env.REACT_APP_INTEGRATION_MICROSERVICE_URL;
const API_BASE = `${INTEGRATION_URL}/browser-desk`;

async function apiFetch(path, options = {}) {
    const user = await getUser();
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.access_token}`,
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const text = await res.text();
        let detail;
        try { detail = JSON.parse(text).detail; } catch { detail = text; }
        const err = new Error(detail || `HTTP ${res.status}`);
        err.status = res.status; // 404/405: an API from before this route; callers fall back
        throw err;
    }
    return res.json();
}

// True for "this API doesn't have the route yet" (deploy order isn't guaranteed).
export const notThere = (e) => e?.status === 404 || e?.status === 405;
const login = (id) => `/logins/${encodeURIComponent(id)}`;

// GET /browser-desk/logins -> { registry_published_at, generated_at, logins: [...] }
export const getLogins = () => apiFetch('/logins');

// POST /browser-desk/logins/{id}/run { job_types, scratch } -> { queued, message }. Refused while
// turned off, over the day's live runs, or scratch for a login not on an office desk. The page
// always names the jobs: an empty list means "every job" to the desk.
export const runNow = (id, { jobTypes, scratch = false }) => apiFetch(`${login(id)}/run`, {
    method: 'POST', body: JSON.stringify({ job_types: jobTypes, scratch: Boolean(scratch) }),
});

// POST /browser-desk/logins/{id}/run-preview { job_types, scratch } -> { jobs: [{ job_type, due_today,
// completed_today, landed_today, expected, missing_last_run }], will_skip, will_fetch_hint, sign_in,
// live_runs_left, refused }. Reads only; queues nothing.
export const getRunPreview = (id, jobTypes, scratch) => apiFetch(`${login(id)}/run-preview`, {
    method: 'POST', body: JSON.stringify({ job_types: jobTypes, scratch: Boolean(scratch) }),
});

// GET /browser-desk/logins/{id}/history?days=14 -> { runs: [...] }, newest first.
export const getHistory = (id, days = 14) => apiFetch(`${login(id)}/history?days=${days}`);

// POST /browser-desk/logins/{id}/skip-week { job_type, reason }: marks a catch-up job done this week.
export const skipWeek = (id, jobType, reason) => apiFetch(`${login(id)}/skip-week`, {
    method: 'POST', body: JSON.stringify({ job_type: jobType, reason }),
});

// POST / DELETE /browser-desk/logins/{id}/stop-today { reason }: no more scheduled runs or retries today.
export const stopToday = (id, reason) => apiFetch(`${login(id)}/stop-today`, { method: 'POST', body: JSON.stringify({ reason }) });
export const resumeToday = (id) => apiFetch(`${login(id)}/stop-today`, { method: 'DELETE' });

// POST /browser-desk/logins/{id}/fix|release { reason }: hand to the dev desk, and back (CLI rules).
export const requestFix = (id, reason) => apiFetch(`${login(id)}/fix`, { method: 'POST', body: JSON.stringify({ reason }) });
export const release = (id, reason) => apiFetch(`${login(id)}/release`, { method: 'POST', body: JSON.stringify({ reason }) });

// POST /browser-desk/logins/{id}/promote { run_id, reason }; GET .../promotions/{run_id} -> { run_id,
// promotion: the worker's result or null, run, files: [{ name, size, last_modified }] } (the test run's files).
export const promote = (id, runId, reason) => apiFetch(`${login(id)}/promote`, {
    method: 'POST', body: JSON.stringify({ run_id: runId, reason }),
});
export const getPromotion = (id, runId) => apiFetch(`${login(id)}/promotions/${encodeURIComponent(runId)}`);

// GET /browser-desk/audit?login=&days= -> audit entries.
export const getAudit = (id, days = 14) => apiFetch(`/audit?login=${encodeURIComponent(id || '')}&days=${days}`);

// POST /browser-desk/logins/{id}/enroll -> { queued, message }. Signs it in and records the device; runs nothing.
export const enrollNow = (id) => apiFetch(`/logins/${encodeURIComponent(id)}/enroll`, {
    method: 'POST', body: JSON.stringify({}),
});

// POST /browser-desk/logins/{id}/disable { reason, until? } — the desk will not run it until turned on.
// `until` (YYYY-MM-DD) is a reminder for people; the desk never turns a login back on by itself.
export const turnOff = (id, reason, until) => apiFetch(`/logins/${encodeURIComponent(id)}/disable`, {
    method: 'POST', body: JSON.stringify(until ? { reason, until } : { reason }),
});

// POST /browser-desk/logins/{id}/enable { reason }: an older API ignores the reason.
export const turnOn = (id, reason) => apiFetch(`/logins/${encodeURIComponent(id)}/enable`, {
    method: 'POST', body: JSON.stringify({ reason: reason || '' }),
});

// PUT /browser-desk/logins/{id}/schedule { days, window: { start, end }, jobs?, reason } — overrides
// the registry's run days and window (and per-job days, newer API) from the next run day. DELETE
// goes back to the registry's.
export const setSchedule = (id, schedule) => apiFetch(`/logins/${encodeURIComponent(id)}/schedule`, {
    method: 'PUT', body: JSON.stringify(schedule),
});
export const resetSchedule = (id) => apiFetch(`/logins/${encodeURIComponent(id)}/schedule`, { method: 'DELETE' });

// GET /browser-desk/logins/{id}/screenshots -> { screenshots: [{ name, last_modified, url }] }, and
// from the newer API also { runs: [{ run_id, day, files }] } under the login's desk scope.
export const getScreenshots = (id) => apiFetch(`/logins/${encodeURIComponent(id)}/screenshots`);

// GET /browser-desk/live/me -> { allowed }: whether this person may open a desk's live screen.
export const getLiveMe = () => apiFetch('/live/me');

// POST /browser-desk/desks/{desk}/live { mode: 'view' | 'control' }
//   -> { ticket, ws_path, expires_in, mode, pod }. A ticket opens one connection, once, within
// expires_in seconds, so every connect and reconnect mints a new one. 403 if not allowlisted.
export const mintLiveTicket = (desk, mode) => apiFetch(`/desks/${encodeURIComponent(desk)}/live`, {
    method: 'POST', body: JSON.stringify({ mode }),
});

// The desk's VNC WebSocket, on the same host as the API (http -> ws, https -> wss).
export const liveSocketUrl = (wsPath, ticket) => `${INTEGRATION_URL.replace(/^http/, 'ws')}${wsPath}?ticket=${encodeURIComponent(ticket)}`;
